/**
 * The product row header's coverage line (BLU-1522 S6a), in plain words.
 *
 * It used to read "0 of 104 SKUs derived": jargon, and for a product on a
 * 3PL's blank a count of the client's OWN code tables, which that product
 * never uses (so a working product read as a failure). Now it says how many
 * variants have a print SKU ready, counted the way the product is built, and
 * when none does, what to do next:
 *
 *   - On the blank of a 3PL: the variants on the chosen blank (`electedVariantCount`).
 *     A blank the 3PL no longer shares (or that is gone, `attention`) is not
 *     ready at all: "Blank no longer available".
 *   - A client that prints only on the blanks of its 3PL, with no blank
 *     chosen yet, while step 1 shows the blank composer: none ready (its own
 *     code tables do not apply).
 *   - Otherwise the variants whose print SKU the org's code tables build
 *     (`derivedCount`); none ready says "Map the missing codes" when a type,
 *     colour or size has no code (step 1's "Map it"), else "Open to see why".
 *
 * "Choose a blank in step 1" is said ONLY when step 1 really offers that
 * choice (`blankStep === 'picker'`). Where step 1 shows something else (a
 * non-admin, a 3PL list that failed or is still loading: the own-blank view;
 * a 3PL sharing no blanks), the line follows what step 1 shows instead.
 *
 * Some ready keeps the bar (the status pill beside it names what is missing);
 * none ready swaps the empty bar for the next step.
 */
import type { PreviewProduct } from './types';

export type CoverageLineKey = 'row.coverageAll' | 'row.coverageSome' | 'row.coverageNone' | 'row.coverageBlankUnavailable';
export type CoverageActionKey = 'row.coverageMapCodes' | 'row.coverageOpenForWhy' | 'row.coverageChooseBlank';

/**
 * What step 1 offers about the 3PL's blank for this product: `picker` = it
 * lets this operator choose (or change) the blank now; `noBlanks` = the blank
 * composer is the body, but no 3PL is sharing blanks; null = neither (the
 * own-blank view, or a saved blank shown read only).
 */
export type BlankStep = 'picker' | 'noBlanks' | null;

export interface CoverageCopy {
  key: CoverageLineKey;
  values: { count: number; ready: number };
  /** The next step when nothing is ready; null when the bar shows instead. */
  actionKey: CoverageActionKey | null;
  /** 0 to 100, for the bar. */
  pct: number;
  complete: boolean;
}

export function coverageCopy(
  product: Pick<PreviewProduct, 'variantCount' | 'derivedCount' | 'unmappedValues'>,
  /** The product's saved 3PL blank (scan summary), or null. */
  supply: { electedVariantCount: number; attention: boolean } | null,
  blankStep: BlankStep,
): CoverageCopy {
  const count = product.variantCount;
  const chooseOrWhy: CoverageActionKey = blankStep === 'picker' ? 'row.coverageChooseBlank' : 'row.coverageOpenForWhy';

  // The 3PL stopped sharing the blank, or it is gone: nothing on it is ready.
  if (supply?.attention) {
    return { key: 'row.coverageBlankUnavailable', values: { count, ready: 0 }, actionKey: chooseOrWhy, pct: 0, complete: false };
  }

  // A supply-only product with no blank yet: step 1 is the blank composer, not the own codes.
  const blankFirst = !supply && blankStep !== null;
  const ready = supply ? supply.electedVariantCount : blankFirst ? 0 : product.derivedCount;
  const complete = count > 0 && ready >= count;
  const pct = count > 0 ? Math.round((Math.min(ready, count) / count) * 100) : 0;
  const values = { count, ready };

  if (complete) return { key: 'row.coverageAll', values, actionKey: null, pct, complete };
  if (ready > 0) return { key: 'row.coverageSome', values, actionKey: null, pct, complete };

  const { productTypes, colours, sizes } = product.unmappedValues;
  // A 3PL's blank never reads the org's own code tables, so it never asks for codes.
  const actionKey: CoverageActionKey = supply
    ? 'row.coverageOpenForWhy'
    : blankFirst
      ? chooseOrWhy
      : productTypes.length + colours.length + sizes.length > 0
        ? 'row.coverageMapCodes'
        : 'row.coverageOpenForWhy';
  return { key: 'row.coverageNone', values, actionKey, pct, complete };
}
